import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import { useTheme } from "@mui/material";
import indexOf from "lodash/indexOf";
import isNil from "lodash/isNil";
import merge from "lodash/merge";
import { useMemo } from "react";
import { grey } from "@mui/material/colors";

const ToggleTabSwitcher = ({
  value,
  tabs,
  onChange,
  sx,
  variant, //background || underline || circle
  useIndex = false,
  fontSize = "13px",
  orientation = "horizontal",
  underlinePosition = "bottom", // new prop: "bottom", "top", "left", "right"
}) => {
  const handleOnTabSwitch = (event, newValue) => {
    if (isNil(newValue)) {
      return;
    }

    if (useIndex) {
      onChange(newValue);
      return;
    }

    onChange(tabs[newValue]);
  };

  const _value = useMemo(() => {
    if (useIndex) {
      return value;
    }
    return indexOf(tabs, value);
  }, [useIndex, tabs, value]);
  return (
    <TabSwitcherGroup
      orientation={orientation}
      variant={variant}
      underlinePosition={underlinePosition}
      value={_value}
      fontSize={fontSize}
      exclusive
      sx={sx}
      onChange={handleOnTabSwitch}
      aria-label="text alignment"
    >
      {tabs.map((tab, ind) => {
        return (
          <ToggleButton key={tab} value={ind}>
            {tab}
          </ToggleButton>
        );
      })}
    </TabSwitcherGroup>
  );
};

const TabSwitcherGroup = ({
  value,
  children,
  full,
  onChange,
  orientation = "horizontal",
  ariaLabel,
  fontSize,
  variant = "background",
  underlinePosition = "bottom", // new prop
  sx,
}) => {
  const theme = useTheme();
  const styles = useMemo(() => {
    const baseStyles = {
      flexGrow: full ? 1 : 0,

      ...(variant === "circle"
        ? {
            flexWrap: "wrap",
          }
        : {}),
      ".MuiButtonBase-root": {
        flexGrow: full ? 1 : 0,
        color: "#181d1f",
        fontWeight: 400,
        lineHeight: 1.2,
        // fontSize: "11px",
        fontSize: fontSize,
        ...(orientation === "vertical" && {
          whiteSpace: "nowrap",
          minWidth: "fit-content",
          justifyContent: "flex-start",
          borderLeft: "2px solid transparent",
        }),
        ...(variant === "circle"
          ? {
              background: "none",
              borderRadius: "50px !important",
              fontWeight: 400,
              flexShrink: 0,
              px: 1.2,
              border: `1px solid ${grey[300]} !important`,
            }
          : {}),
        "&.Mui-selected": {
          color: "#2962ff",
          fontWeight: 700,
          // Override default background for variants
          ...(variant === "underline"
            ? {
                background: "none",
                borderRadius: "0 !important",
                fontWeight: 500,
              }
            : {
                background: "#2962ff14",
              }),

          // circle variant active styles
          ...(variant === "circle"
            ? {
                // background: "none",
                // borderRadius: "50px !important",
                fontWeight: `600 !important`,
                border: "1px solid #2962ff !important",
              }
            : {
                // background: "#2962ff14",
                // border: "1px solid #dddddd",
              }),

          // Handle border placement based on orientation and position
          ...(variant === "underline" && {
            borderTop: "none",
            borderRight: "none",
            borderBottom: "none",
            borderLeft: "none",
            ...(orientation === "horizontal" &&
              underlinePosition === "bottom" && {
                borderBottom: "2px solid #2962ff",
              }),
            ...(orientation === "horizontal" &&
              underlinePosition === "top" && {
                borderTop: "2px solid #2962ff",
              }),
            ...(orientation === "vertical" &&
              underlinePosition === "left" && {
                borderLeft: "2px solid #2962ff",
              }),
            ...(orientation === "vertical" &&
              underlinePosition === "right" && {
                borderRight: "2px solid #2962ff",
              }),
          }),
        },
      },

      "& .MuiToggleButtonGroup-grouped": {
        margin: theme?.spacing(0.2),
        border: 0,
        borderLeft: "2px solid transparent",
        textTransform: "capitalize",
        "&.Mui-disabled": {
          border: 0,
        },
        "&:not(:first-of-type)": {
          borderRadius: "4px",
        },
        "&:first-of-type": {
          borderRadius: "4px",
        },
      },
    };

    // ------------
    // VARIANTS:
    // ------------
    const variants = {
      underline: {
        ".MuiButtonBase-root": {
          fontSize: "11px",
        },
      },
    };

    const variantStyles = variants[variant] ?? {};

    const mergedStyles = merge(merge({}, baseStyles), variantStyles);
    const finalStyles = merge(mergedStyles, sx);
    return finalStyles;
  }, [sx, variant, full, theme, fontSize, orientation, underlinePosition]);

  return (
    <ToggleButtonGroup
      size="small"
      className="toggle-tab-switcher-root"
      orientation={orientation}
      value={value}
      exclusive
      onChange={onChange}
      aria-label={ariaLabel}
      sx={styles}
    >
      {children}
    </ToggleButtonGroup>
  );
};

export default ToggleTabSwitcher;
